/**
 * In-app card camera: live preview with a card-frame guide, capture, and retake.
 * Used for dual-side card uploads (表面 / 裏面) on mobile.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isCardAlignedInGuide } from './cardGuideAlignment';
import './CardCameraCapture.css';

export type CardCameraSide = 'front' | 'back';

export interface CardCameraCaptureProps {
  side: CardCameraSide;
  /** Localized side label (e.g. 表面 / 裏面) */
  sideLabel: string;
  /** Tip under the frame */
  tipText: string;
  captureLabel: string;
  usePhotoLabel: string;
  retakeLabel: string;
  cancelLabel: string;
  errorPermissionLabel: string;
  errorGenericLabel: string;
  fallbackLabel: string;
  onCaptured: (file: File) => void;
  onCancel: () => void;
  /** Fall back to native file/camera picker */
  onFallback: () => void;
}

type Phase = 'starting' | 'live' | 'preview' | 'error';
type GuideColor = 'red' | 'green';

const ALIGN_POLL_MS = 120;
/** Consecutive hits needed before switching guide color (reduces flicker). */
const ALIGN_CONFIRM_FRAMES = 3;

/** Map a screen rect onto video pixel coords when the video uses object-fit: cover. */
function guideRectToVideoCrop(
  video: HTMLVideoElement,
  guide: DOMRect
): { sx: number; sy: number; sw: number; sh: number } {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const videoRect = video.getBoundingClientRect();
  const elW = videoRect.width;
  const elH = videoRect.height;
  if (!vw || !vh || !elW || !elH) {
    return { sx: 0, sy: 0, sw: vw || 0, sh: vh || 0 };
  }

  const scale = Math.max(elW / vw, elH / vh);
  const offsetX = (elW - vw * scale) / 2;
  const offsetY = (elH - vh * scale) / 2;

  let sx = (guide.left - videoRect.left - offsetX) / scale;
  let sy = (guide.top - videoRect.top - offsetY) / scale;
  let sw = guide.width / scale;
  let sh = guide.height / scale;

  sx = Math.max(0, Math.min(sx, vw));
  sy = Math.max(0, Math.min(sy, vh));
  sw = Math.max(1, Math.min(sw, vw - sx));
  sh = Math.max(1, Math.min(sh, vh - sy));

  return { sx, sy, sw, sh };
}

const CardCameraCapture: React.FC<CardCameraCaptureProps> = ({
  side,
  sideLabel,
  tipText,
  captureLabel,
  usePhotoLabel,
  retakeLabel,
  cancelLabel,
  errorPermissionLabel,
  errorGenericLabel,
  fallbackLabel,
  onCaptured,
  onCancel,
  onFallback,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analyzeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const alignStreakRef = useRef({ green: 0, red: 0 });
  const [phase, setPhase] = useState<Phase>('starting');
  const [errorKind, setErrorKind] = useState<'permission' | 'generic'>('generic');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [guideColor, setGuideColor] = useState<GuideColor>('red');

  const stopStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      video.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setPhase('starting');
    setErrorKind('generic');
    setGuideColor('red');
    alignStreakRef.current = { green: 0, red: 0 };
    stopStream();
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorKind('generic');
      setPhase('error');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play().catch(() => undefined);
      }
      setPhase('live');
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      setErrorKind(
        name === 'NotAllowedError' || name === 'PermissionDeniedError' ? 'permission' : 'generic'
      );
      setPhase('error');
    }
  }, [stopStream]);

  useEffect(() => {
    void startCamera();
    return () => {
      stopStream();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once per open
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Poll guide alignment while live
  useEffect(() => {
    if (phase !== 'live') return;
    if (!analyzeCanvasRef.current) {
      analyzeCanvasRef.current = document.createElement('canvas');
    }
    const scratch = analyzeCanvasRef.current;
    const id = window.setInterval(() => {
      const video = videoRef.current;
      const frameEl = frameRef.current;
      if (!video || !frameEl || video.readyState < 2) return;
      const crop = guideRectToVideoCrop(video, frameEl.getBoundingClientRect());
      const aligned = isCardAlignedInGuide(video, crop, scratch);
      const streak = alignStreakRef.current;
      if (aligned) {
        streak.green += 1;
        streak.red = 0;
        if (streak.green >= ALIGN_CONFIRM_FRAMES) {
          setGuideColor((prev) => (prev === 'green' ? prev : 'green'));
        }
      } else {
        streak.red += 1;
        streak.green = 0;
        if (streak.red >= ALIGN_CONFIRM_FRAMES) {
          setGuideColor((prev) => (prev === 'red' ? prev : 'red'));
        }
      }
    }, ALIGN_POLL_MS);
    return () => window.clearInterval(id);
  }, [phase]);

  const handleCapture = async () => {
    const video = videoRef.current;
    const frameEl = frameRef.current;
    if (!video || !frameEl || busy || phase !== 'live') return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    setBusy(true);
    try {
      const { sx, sy, sw, sh } = guideRectToVideoCrop(video, frameEl.getBoundingClientRect());
      const outW = Math.round(sw);
      const outH = Math.round(sh);
      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92)
      );
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      setPreviewBlob(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      stopStream();
      setPhase('preview');
    } finally {
      setBusy(false);
    }
  };

  const handleRetake = () => {
    setPreviewBlob(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    void startCamera();
  };

  const handleUsePhoto = () => {
    if (!previewBlob) return;
    const file = new File([previewBlob], `card_${side}_${Date.now()}.jpg`, {
      type: 'image/jpeg',
    });
    onCaptured(file);
  };

  const frameClass =
    guideColor === 'green'
      ? 'card-camera__frame card-camera__frame--aligned'
      : 'card-camera__frame card-camera__frame--misaligned';

  return (
    <div className="card-camera" role="dialog" aria-modal="true" aria-label={sideLabel}>
      <div className="card-camera__stage">
        {phase !== 'preview' && (
          <video
            ref={videoRef}
            className="card-camera__video"
            playsInline
            muted
            autoPlay
            aria-hidden={phase !== 'live'}
          />
        )}
        {phase === 'preview' && previewUrl && (
          <img
            src={previewUrl}
            alt={sideLabel}
            className="card-camera__preview-img card-camera__preview-img--cropped"
          />
        )}

        {(phase === 'live' || phase === 'starting') && (
          <div className="card-camera__overlay" aria-hidden>
            <div className="card-camera__frame-wrap">
              <div ref={frameRef} className={frameClass}>
                <span className="card-camera__corner card-camera__corner--tl" />
                <span className="card-camera__corner card-camera__corner--tr" />
                <span className="card-camera__corner card-camera__corner--bl" />
                <span className="card-camera__corner card-camera__corner--br" />
              </div>
            </div>
          </div>
        )}

        <div className="card-camera__top">
          <span className="card-camera__side-badge">{sideLabel}</span>
          <button type="button" className="card-camera__close" onClick={onCancel} aria-label={cancelLabel}>
            ×
          </button>
        </div>

        {(phase === 'live' || phase === 'starting') && (
          <p className="card-camera__tip">{tipText}</p>
        )}

        {phase === 'error' && (
          <div className="card-camera__error">
            <p>
              {errorKind === 'permission' ? errorPermissionLabel : errorGenericLabel}
            </p>
            <button type="button" className="card-camera__btn card-camera__btn--secondary" onClick={onFallback}>
              {fallbackLabel}
            </button>
          </div>
        )}
      </div>

      <div className="card-camera__controls">
        {phase === 'live' && (
          <button
            type="button"
            className="card-camera__shutter"
            onClick={handleCapture}
            disabled={busy}
            aria-label={captureLabel}
          />
        )}
        {phase === 'preview' && (
          <>
            <button type="button" className="card-camera__btn card-camera__btn--secondary" onClick={handleRetake}>
              {retakeLabel}
            </button>
            <button type="button" className="card-camera__btn card-camera__btn--primary" onClick={handleUsePhoto}>
              {usePhotoLabel}
            </button>
          </>
        )}
        {(phase === 'starting' || phase === 'error') && (
          <button type="button" className="card-camera__btn card-camera__btn--secondary" onClick={onCancel}>
            {cancelLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default CardCameraCapture;
